import React from 'react';
import css from './SocialMediaFormFields.module.css';
import { FormattedMessage, useIntl } from 'react-intl';
import TitleInputField from '../TitleInputField';
import PriceInputField from '../PriceInputField';
import DateNeededInputField from '../DateNeededInputField';
import PersonalizationFormField from '../PersonalizationFormField';
import HashtagsFormField from '../HashtagsFormField';
import { SocialMediaContentTypes } from '../../../../util/enums';
import UploadImagesField from '../UploadImagesField/UploadImagesField';
import UrlInputField from '../UrlInputField/UrlInputField';
import BookingTimeFormField from '../BookingTimeFormField/BookingTimeFormField';

const SocialMediaFormFields = ({ formId, values, platform, listing }) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const { title } = values || {};
  const {
    POST,
    REEL,
    SHORT,
    STORY,
    SPOTLIGHT,
    LIVE,
    ARTICLE,
    RECOMMENDATION,
  } = SocialMediaContentTypes;
  // Detect content type from title (you can adjust logic as needed)
  const getContentType = () => {
    if (!title) return POST;
    const lower = title.toLowerCase();
    if (lower.includes(STORY)) return STORY;
    if (lower.includes(REEL)) return REEL;
    if (lower.includes(SHORT)) return SHORT;
    if (lower.includes(SPOTLIGHT)) return SPOTLIGHT;
    if (lower.includes(LIVE) || lower.includes('q&a')) return LIVE;
    if (lower.includes(ARTICLE)) return ARTICLE;
    if (lower.includes(RECOMMENDATION)) return RECOMMENDATION;
    return POST;
  };

  const type = getContentType();

  // Build dynamic keys
  const dateLabelKey = `DateNeededInputField.label.${
    type === LIVE ? LIVE : type === ARTICLE ? ARTICLE : type
  }`;
  const textLabelKey =
    type === LIVE
      ? 'PersonalizationFormField.label.liveDescription'
      : type === RECOMMENDATION
      ? 'PersonalizationFormField.label.recommendation'
      : type === ARTICLE
      ? 'PersonalizationFormField.label.article'
      : `PersonalizationFormField.label.${type}`;

  const textPlaceholderKey = `PersonalizationFormField.placeholder.${
    type === LIVE ? 'appearance' : type
  }`;
  const isVideoContent =
    [POST, REEL, SHORT].includes(type) &&
    ['YouTube', 'LinkedIn', 'Twitter', 'Pinterest', 'Meta'].includes(platform);

  const imageUploadLabelMap = {
    [POST]: 'UploadImagesField.label.post',
    [REEL]: 'UploadImagesField.label.reel',
    [SHORT]: 'UploadImagesField.label.short',
    [STORY]: 'UploadImagesField.label.story',
    [SPOTLIGHT]: 'UploadImagesField.label.spotlight',
    [ARTICLE]: 'UploadImagesField.label.article',
  };

  const getImageUploadLabelId = () =>
    imageUploadLabelMap[type] || 'UploadImagesField.label.default';
  // Default fallback
  const hideDateField =
    ['LinkedIn', 'Facebook', 'YouTube', 'TikTok'].includes(platform) &&
    ['recommendation', 'live'].includes(type);
  const hideImagesField =
    ['LinkedIn', 'Facebook', 'Instagram', 'YouTube', 'TikTok'].includes(platform) &&
    ['recommendation', 'live'].includes(type);
  const hideHashtagField =
    ['LinkedIn', 'Facebook', 'YouTube', 'TikTok'].includes(platform) &&
    ['recommendation', 'live', 'article'].includes(type);
  const showLinkedInUrlField = ['LinkedIn'].includes(platform) && ['recommendation'].includes(type);
  const showBookingTimeFormField =
    ['Facebook', 'YouTube', 'TikTok'].includes(platform) && ['live'].includes(type);

  return (
    <>
      <div className={css.offeringBlock}>
        <div className={css.offeringHeader}>
          <h4>
            <FormattedMessage id="SocialMediaFormFields.sectionHeading" />
          </h4>
        </div>
      </div>
      <div className={css.formRow}>
        <div className={css.formFld}>
          {/* Title */}
          <TitleInputField label={'Type'} id={'type'} fieldName={'title'} />
        </div>
        <div className={css.formFld}>
          {/* PlatForm */}
          {platform && (
            <TitleInputField label={'Platform'} id={'platform'} fieldName={'platform'} />
          )}
        </div>
      </div>
      <div className={css.formRow}>
        <div className={css.formFld}>
          {/* Price */}
          <PriceInputField formId={formId} />{' '}
        </div>
        <div className={css.formFld}>
          {/* Dynamic Date Field */}
          {!hideDateField && (
            <DateNeededInputField
              label={formatMessage({
                id: dateLabelKey,
              })}
              fieldName="dateNeededBy"
            />
          )}
        </div>
      </div>

      {/* Booking Time */}
      {showBookingTimeFormField && <BookingTimeFormField listing={listing} />}

      {/* Dynamic Personalization Text */}
      <PersonalizationFormField
        label={formatMessage({
          id: textLabelKey,
        })}
        placeholder={formatMessage({
          id: textPlaceholderKey,
        })}
        fieldName="personalizationText"
      />

      {/* Optional: Appearance description for video-based posts */}
      {isVideoContent && (
        <PersonalizationFormField
          label={formatMessage({
            id: 'PersonalizationFormField.label.appearance',
          })}
          placeholder={formatMessage({
            id: 'PersonalizationFormField.placeholder.appearance',
          })}
          fieldName="appearanceInstructions"
        />
      )}
      {showLinkedInUrlField && (
        <UrlInputField
          label={formatMessage({ id: 'UrlInputField.LinkedIn.label' })}
          fieldName="linkedinProfileUrl"
        />
      )}
      {/* Upload Images */}
      {!hideImagesField && (
        <UploadImagesField
          fieldName="uploadedImages"
          label={formatMessage({ id: getImageUploadLabelId() })}
        />
      )}
      {/* Hashtags */}
      {!hideHashtagField && (
        <HashtagsFormField
          label={formatMessage({
            id: 'SocialMediaFormFields.HashtagsFormField.label',
          })}
          placeholder={formatMessage({
            id: 'SocialMediaFormFields.HashtagsFormField.placeholder',
          })}
        />
      )}
    </>
  );
};

export default SocialMediaFormFields;
